// src/contexts/ExercisesContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { loadProgress, injectMidiExercisesToProgress, saveProgress } from '../utils/progressStorage';
import { onAuthStateChanged } from 'firebase/auth';
import MonitoringService from '../services/MonitoringService';

const ExercisesContext = createContext();

export const ExercisesProvider = ({ children }) => {
  const [allExercises, setAllExercises] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [authUser, setAuthUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

useEffect(() => {
  if (!authReady) return;
  if (!authUser) {
    setIsLoading(false);
    return;
  }
  const loadExercises = async () => {
    try {
      console.log('[Exercises] loadExercises START → isLoading=true', { userId: authUser?.uid });
      setIsLoading(true);

      // Créer la structure pour la configuration
      const midiConfig = { exercises: [] };
      
      // Récupérer tous les exercices depuis Firestore (sans orderBy pour éviter les index composites)
      // Mode gratuit (anonyme) : les règles Firestore n'autorisent que freeTier == true ; une lecture non bornée échouerait en entier
      const exercisesQuery = authUser.isAnonymous === true
        ? query(collection(db, 'exercises'), where('freeTier', '==', true))
        : collection(db, 'exercises');

      const querySnapshot = await getDocs(exercisesQuery);

      const loadedExercises = {};
      
      // Créer la liste des exercices pour la configuration
      querySnapshot.forEach(doc => {
        const exerciseData = doc.data();
        const exerciseId = doc.id;
        
        // Ajouter à la configuration pour la progression
        midiConfig.exercises.push({
          id: exerciseId,
          nodeId: exerciseData.nodeId,
          type: exerciseData.type || 'standard',
          title: exerciseData.name || 'Exercice MIDI',
          fileUrl: exerciseData.fileUrl,
          filename: exerciseData.name ? `${exerciseData.name}.mid` : 'exercice.mid'
        });
        
        // Ajouter à notre objet d'exercices
        loadedExercises[exerciseId] = {
          id: exerciseId,
          title: exerciseData.name || 'Exercice MIDI',
          name: exerciseData.name || 'Exercice MIDI',
          type: exerciseData.type || 'standard',
          nodeId: exerciseData.nodeId,
          categoryId: exerciseData.categoryId,
          order: exerciseData.order || 0,
          fileUrl: exerciseData.fileUrl,
          // Charger config et index depuis Firestore
          config: exerciseData.config || {
            high: [],
            low: []
          },
          index: exerciseData.index || []
        };
      });

      // Injecter les exercices dans la progression locale
      const progress = loadProgress();
      const updatedProgress = injectMidiExercisesToProgress(progress, midiConfig);
      saveProgress(updatedProgress);

      // IMPORTANT: Utilisez uniquement les exercices chargés, sans les combiner avec baseExercises

      setAllExercises(loadedExercises);
      console.log('[Exercises] loadExercises DONE → isLoading=false', { count: Object.keys(loadedExercises).length });
      setIsLoading(false);
      MonitoringService.info('exercises_loaded', { count: Object.keys(loadedExercises).length }, authUser?.uid);
    } catch (error) {
      console.error('[Exercises] loadExercises ERROR → isLoading=false', error);
      setLoadingError(error.message);
      setIsLoading(false);
      MonitoringService.error('exercises_load_failed', {
        errorMessage: error.message,
        errorCode: error.code || null,
      }, authUser?.uid);
    }
  };
  
  loadExercises();
}, [authReady, authUser?.uid, authUser?.isAnonymous]);
  
  // Fonction pour charger les exercices d'un nœud spécifique
// Dans ExercisesContext.js
const getExercisesByNode = async (nodeId) => {
  try {
    // Vérifier si nous avons déjà les exercices pour ce nœud dans l'état
    const existingExercises = Object.values(allExercises)
      .filter(ex => ex.nodeId === nodeId);

    if (existingExercises.length > 0) {
      return existingExercises;
    }

    // Sinon, charger depuis Firestore
    const anonymous = authUser?.isAnonymous === true;
    // Anonyme : requête freeTier seule (pas d'index composite), filtre/tri côté client
    const nodeExercisesQuery = anonymous
      ? query(collection(db, 'exercises'), where('freeTier', '==', true))
      : query(collection(db, 'exercises'), where('nodeId', '==', nodeId), orderBy('order', 'asc'));

    const querySnapshot = await getDocs(nodeExercisesQuery);

    let nodeExercises = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    if (anonymous) {
      nodeExercises = nodeExercises
        .filter(ex => ex.nodeId === nodeId)
        .sort((x, y) => (x.order || 0) - (y.order || 0));
    }
    
    // Mettre à jour l'état global de manière sécurisée
    setAllExercises(prev => {
      const updated = { ...prev };
      nodeExercises.forEach(ex => {
        updated[ex.id] = ex;
      });
      return updated;
    });

    return nodeExercises;
  } catch (error) {
    console.error(`Erreur lors du chargement des exercices pour le nœud ${nodeId}:`, error);
    MonitoringService.error('exercises_node_load_failed', {
      nodeId,
      errorMessage: error.message,
      errorCode: error.code || null,
    });
    return [];
  }
};
  
  // Fonction pour récupérer un exercice par son ID
  const getExerciseById = (exerciseId) => {
    return allExercises[exerciseId] || null;
  };

  return (
    <ExercisesContext.Provider value={{
      exercises: allExercises,
      isLoading,
      loadingError,
      getExercisesByNode,
      getExerciseById
    }}>
      {children}
    </ExercisesContext.Provider>
  );
};

export const useExercises = () => useContext(ExercisesContext);